/* eslint-disable unicode-bom */

// ============================================================================
// GiftPacking.js — ISHMAI Admin Panel
// ----------------------------------------------------------------------------
// Admin-only "Gift Packing" page: manage gift wrapping options and pricing.
// Follows the same structure as the existing SuperCoins.js admin page —
// summary cards, a searchable/filterable/paginated table, and a full-width
// Add/Edit form that swaps in via local state (not a modal).
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import {
  getGiftPackingStatistics,
  getGiftPackingSummary,
  getGiftPackingList,
  getGiftPackingDetails,
  createGiftPacking,
  updateGiftPacking,
  toggleGiftPackingStatus,
  deleteGiftPacking,
} from '../services/api';
import './Gift.css';

// ---------------------------------------------------------------------------
// Image URL Helper
// ---------------------------------------------------------------------------

var IMAGE_BASE_URL = (
  process.env.VITE_BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_IMAGE_URL ||
  'https://study.richmindx.in'
).replace(/\/+$/, '');

var resolveImageUrl = function (url) {
  if (!url || typeof url !== 'string' || !url.trim()) return '';
  var u = url.trim();
  if (u.startsWith('blob:') || u.startsWith('data:') || u.startsWith('http://') || u.startsWith('https://')) return u;
  u = u.replace(/\\/g, '/');
  var uploadsIndex = u.toLowerCase().indexOf('/uploads/');
  if (uploadsIndex >= 0) u = u.slice(uploadsIndex + 1);
  u = u.replace(/^public\//, '');
  if (u.indexOf('/') === -1 && /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(u)) {
    u = 'uploads/' + u;
  }
  return IMAGE_BASE_URL + '/' + u.replace(/^\/+/, '');
};

// ---------------------------------------------------------------------------
// Constants / helpers
// ---------------------------------------------------------------------------

var GIFT_PER_PAGE_OPTIONS = [10, 25, 50];
var STATUS_FILTERS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'orders', label: 'Orders With Gift Packing' },
  { value: 'revenue', label: 'Gift Packing Revenue' },
];

var formatINR = function (n) {
  var num = Number(n || 0);
  return '\u20B9' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
var formatNum = function (n) { return Number(n || 0).toLocaleString('en-IN'); };
var totalPages = function (count, perPage) { return Math.max(1, Math.ceil((count || 0) / perPage)); };
var isActiveStatus = function (status) { return String(status || '').toLowerCase() === 'active'; };

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

var Icon = function (props) {
  var children = props.children; var size = props.size || 15;
  return React.createElement('svg', {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  }, children);
};

var GiftTagIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('polyline', { points: '20 12 20 22 4 22 4 12' }),
    React.createElement('rect', { x: 2, y: 7, width: 20, height: 5 }),
    React.createElement('line', { x1: 12, y1: 22, x2: 12, y2: 7 }),
    React.createElement('path', { d: 'M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z' }),
    React.createElement('path', { d: 'M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z' })
  );
};

var ToggleCircleIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('rect', { x: 2, y: 7, width: 20, height: 10, rx: 5 }),
    React.createElement('circle', { cx: 8, cy: 12, r: 3 })
  );
};

var BagIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('path', { d: 'M6 7h12l1 13H5z' }),
    React.createElement('path', { d: 'M9 7a3 3 0 0 1 6 0' })
  );
};

var RupeeStackIcon = function (props) {
  return React.createElement(Icon, { size: props.size },
    React.createElement('path', { d: 'M7 4h9' }),
    React.createElement('path', { d: 'M7 8h9' }),
    React.createElement('path', { d: 'M7 4c0 5 9 4 9 9s-9 4-9 9' })
  );
};

var SearchIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('circle', { cx: 11, cy: 11, r: 8 }),
    React.createElement('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
  );
};

var PlusIcon = function () {
  return React.createElement(Icon, { size: 15 },
    React.createElement('line', { x1: 12, y1: 5, x2: 12, y2: 19 }),
    React.createElement('line', { x1: 5, y1: 12, x2: 19, y2: 12 })
  );
};

var ArrowLeftIcon = function () {
  return React.createElement(Icon, { size: 15 },
    React.createElement('line', { x1: 19, y1: 12, x2: 5, y2: 12 }),
    React.createElement('polyline', { points: '12 19 5 12 12 5' })
  );
};

var ChevronLeftIcon = function () {
  return React.createElement(Icon, { size: 15 }, React.createElement('polyline', { points: '15 18 9 12 15 6' }));
};

var ChevronRightIcon = function () {
  return React.createElement(Icon, { size: 15 }, React.createElement('polyline', { points: '9 18 15 12 9 6' }));
};

var EyeIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('path', { d: 'M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};

var PencilIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('path', { d: 'M12 20h9' }),
    React.createElement('path', { d: 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z' })
  );
};

var TrashIcon = function () {
  return React.createElement(Icon, { size: 14 },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6' }),
    React.createElement('path', { d: 'M14 11v6' }),
    React.createElement('path', { d: 'M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2' })
  );
};

var UploadCloudIcon = function () {
  return React.createElement(Icon, { size: 22 },
    React.createElement('path', { d: 'M16 16l-4-4-4 4' }),
    React.createElement('path', { d: 'M12 12v9' }),
    React.createElement('path', { d: 'M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3' })
  );
};

var CheckCircleIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('path', { d: 'M22 11.08V12a10 10 0 1 1-5.93-9.14' }),
    React.createElement('polyline', { points: '22 4 12 14.01 9 11.01' })
  );
};

var AlertCircleIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
    React.createElement('line', { x1: 12, y1: 8, x2: 12, y2: 12 }),
    React.createElement('line', { x1: 12, y1: 16, x2: 12.01, y2: 16 })
  );
};

var XIcon = function () {
  return React.createElement(Icon, { size: 16 },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};

// ---------------------------------------------------------------------------
// Small reusable primitives
// ---------------------------------------------------------------------------

var Toast = function (props) {
  var toast = props.toast; var onClose = props.onClose;
  useEffect(function () {
    if (!toast) return undefined;
    var t = setTimeout(onClose, 3500);
    return function () { clearTimeout(t); };
  }, [toast, onClose]);
  if (!toast) return null;
  return React.createElement('div', { className: 'gp-toast gp-toast-' + toast.type },
    toast.type === 'success' ? React.createElement(CheckCircleIcon, null) : React.createElement(AlertCircleIcon, null),
    React.createElement('span', null, toast.message),
    React.createElement('button', { className: 'gp-toast-close', type: 'button', onClick: onClose }, React.createElement(XIcon, null))
  );
};

var Pagination = function (props) {
  var page = props.page; var pages = props.pages; var onChange = props.onChange;
  if (pages <= 1) return null;
  var nums = [];
  for (var i = 1; i <= pages; i++) nums.push(i);
  var visible = nums.slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5);
  return React.createElement('div', { className: 'gp-pagination' },
    React.createElement('button', { className: 'gp-page-btn', type: 'button', disabled: page <= 1, onClick: function () { onChange(page - 1); } }, React.createElement(ChevronLeftIcon, null)),
    visible.map(function (p) {
      return React.createElement('button', {
        key: p, className: 'gp-page-btn' + (p === page ? ' is-active' : ''),
        onClick: function () { onChange(p); },
      }, p);
    }),
    React.createElement('button', { className: 'gp-page-btn', type: 'button', disabled: page >= pages, onClick: function () { onChange(page + 1); } }, React.createElement(ChevronRightIcon, null))
  );
};

var SummaryCard = function (props) {
  var isClickable = !!props.onClick;
  var isActive = !!props.isActive;
  return React.createElement('div', {
    className: 'gp-summary-card' + (isClickable ? ' gp-summary-card-clickable' : '') + (isActive ? ' is-active' : ''),
    onClick: props.onClick,
    role: isClickable ? 'button' : undefined,
    tabIndex: isClickable ? 0 : undefined
  },
    React.createElement('div', { className: 'gp-card-icon ' + (props.iconClass || '') }, props.icon),
    React.createElement('div', { className: 'gp-card-content' },
      React.createElement('div', { className: 'gp-card-label' }, props.label),
      React.createElement('div', { className: 'gp-card-val' }, props.value),
      props.hint && React.createElement('div', { className: 'gp-card-hint' }, props.hint)
    )
  );
};

var Breadcrumb = function (props) {
  return React.createElement('div', { className: 'gp-breadcrumb' },
    props.items.map(function (item, i) {
      return React.createElement(React.Fragment, { key: i },
        i > 0 && React.createElement('span', { className: 'gp-breadcrumb-sep' }, '\u203A'),
        item.onClick
          ? React.createElement('button', { type: 'button', className: 'gp-breadcrumb-link', onClick: item.onClick }, item.label)
          : React.createElement('span', { className: 'gp-breadcrumb-current' }, item.label)
      );
    })
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

var GiftPacking = function () {
  // 'list' | 'add' | 'edit'
  var viewState = useState('list'); var view = viewState[0]; var setView = viewState[1];
  var activeItemIdState = useState(null); var activeItemId = activeItemIdState[0]; var setActiveItemId = activeItemIdState[1];
  var showUpdateSuccessState = useState(false); var showUpdateSuccess = showUpdateSuccessState[0]; var setShowUpdateSuccess = showUpdateSuccessState[1];

  var toastState = useState(null); var toast = toastState[0]; var setToast = toastState[1];
  var showToast = function (type, message) { setToast({ type: type, message: message }); };

  var goToList = function () { setView('list'); setActiveItemId(null); };
  var goToAdd = function () { setView('add'); setActiveItemId(null); };
  var goToEdit = function (id) { setActiveItemId(id); setView('edit'); };

  // -- summary --
  var summaryState = useState(null); var summary = summaryState[0]; var setSummary = summaryState[1];

  // -- table --
  var itemsState = useState([]); var items = itemsState[0]; var setItems = itemsState[1];
  var itemsTotalState = useState(0); var itemsTotal = itemsTotalState[0]; var setItemsTotal = itemsTotalState[1];
  var itemsPagesState = useState(1); var serverPages = itemsPagesState[0]; var setServerPages = itemsPagesState[1];
  var itemsLoadingState = useState(true); var itemsLoading = itemsLoadingState[0]; var setItemsLoading = itemsLoadingState[1];
  var itemsErrorState = useState(''); var itemsError = itemsErrorState[0]; var setItemsError = itemsErrorState[1];
  var searchState = useState(''); var search = searchState[0]; var setSearch = searchState[1];
  var searchDebState = useState(''); var searchDeb = searchDebState[0]; var setSearchDeb = searchDebState[1];
  var statusFilterState = useState('all'); var statusFilter = statusFilterState[0]; var setStatusFilter = statusFilterState[1];
  var pageState = useState(1); var page = pageState[0]; var setPage = pageState[1];
  var perPageState = useState(10); var perPage = perPageState[0]; var setPerPage = perPageState[1];

  var fetchSummary = useCallback(function () {
    (getGiftPackingStatistics ? getGiftPackingStatistics() : getGiftPackingSummary())
      .then(function (res) {
        var d = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || {};
        setSummary({
          totalGiftPackingTypes: d.totalGiftPackingTypes !== undefined ? d.totalGiftPackingTypes : (d.totalTypes !== undefined ? d.totalTypes : 0),
          activeGiftPackingTypes: d.activeGiftPackingTypes !== undefined ? d.activeGiftPackingTypes : (d.activeTypes !== undefined ? d.activeTypes : 0),
          ordersWithGiftPacking: d.ordersWithGiftPacking !== undefined ? d.ordersWithGiftPacking : (d.orders !== undefined ? d.orders : 0),
          totalGiftPackingRevenue: d.totalGiftPackingRevenue !== undefined ? d.totalGiftPackingRevenue : (d.totalRevenue !== undefined ? d.totalRevenue : 0),
        });
      })
      .catch(function (e) {
        console.error('Failed to load Gift Packing summary/statistics:', e);
      });
  }, []);

  var fetchItems = useCallback(function () {
    setItemsLoading(true);
    setItemsError('');

    var params = {
      page: page,
      limit: perPage,
    };
    if (searchDeb && searchDeb.trim()) params.search = searchDeb.trim();
    if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

    getGiftPackingList(params)
      .then(function (res) {
        var body = res && res.data;
        var list = [];
        var total = 0;
        var pagesCount = 1;

        if (body) {
          if (Array.isArray(body.data)) {
            list = body.data;
            total = body.total !== undefined ? body.total : (body.count !== undefined ? body.count : list.length);
            pagesCount = body.pages !== undefined ? body.pages : totalPages(total, perPage);
          } else if (body.data && Array.isArray(body.data.list)) {
            list = body.data.list;
            total = body.data.total !== undefined ? body.data.total : list.length;
            pagesCount = body.data.pages !== undefined ? body.data.pages : totalPages(total, perPage);
          } else if (Array.isArray(body)) {
            list = body;
            total = body.length;
            pagesCount = totalPages(total, perPage);
          }
        }

        // Apply client-side filter if backend returns list or for custom status filters
        if (statusFilter === 'active') {
          list = list.filter(function (it) { return isActiveStatus(it.status); });
        } else if (statusFilter === 'inactive') {
          list = list.filter(function (it) { return !isActiveStatus(it.status); });
        } else if (statusFilter === 'orders') {
          list = list.filter(function (it) {
            var cnt = Number(it.ordersCount || it.totalOrders || it.orders || it.usageCount || it.salesCount || it.count || 0);
            return cnt > 0 || isActiveStatus(it.status);
          });
        } else if (statusFilter === 'revenue') {
          list = list.filter(function (it) {
            var rev = Number(it.totalRevenue || it.revenue || 0);
            var price = Number(it.price || 0);
            return rev > 0 || price > 0;
          });
        }

        setItems(list);
        setItemsTotal(total);
        setServerPages(pagesCount);
      })
      .catch(function (e) {
        console.error('Failed to load Gift Packing types:', e);
        var msg = (e && e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Failed to load gift packing types. Please try again.';
        setItemsError(msg);
        setItems([]);
      })
      .finally(function () { setItemsLoading(false); });
  }, [searchDeb, statusFilter, page, perPage]);

  useEffect(function () { fetchSummary(); }, [fetchSummary]);
  useEffect(function () { fetchItems(); }, [fetchItems]);

  useEffect(function () {
    var t = setTimeout(function () { setPage(1); setSearchDeb(search); }, 400);
    return function () { clearTimeout(t); };
  }, [search]);

  var refreshAll = function () { fetchSummary(); fetchItems(); };

  var handleToggleStatus = function (item) {
    var nextStatus = isActiveStatus(item.status) ? 'inactive' : 'active';
    toggleGiftPackingStatus(item._id || item.id, nextStatus)
      .then(function (res) {
        var msg = (res && res.data && res.data.message) || ((item.name || 'Gift packing option') + ' status changed to ' + nextStatus);
        showToast('success', msg);
        refreshAll();
      })
      .catch(function (e) {
        var msg = (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Failed to update status';
        showToast('error', msg);
        console.error('Toggle Gift Packing status error:', (e.response && e.response.data) || e.message);
      });
  };

  var handleDelete = function (item) {
    if (typeof window !== 'undefined' && !window.confirm('Delete "' + (item.name || 'this gift packing option') + '"? This cannot be undone.')) return;
    deleteGiftPacking(item._id || item.id)
      .then(function (res) {
        var msg = (res && res.data && res.data.message) || ((item.name || 'Gift packing option') + ' deleted.');
        showToast('success', msg);
        refreshAll();
      })
      .catch(function (e) {
        var msg = (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Failed to delete gift packing option';
        showToast('error', msg);
        console.error('Delete Gift Packing error:', (e.response && e.response.data) || e.message);
      });
  };

  var itemsPages = Math.max(serverPages || 1, totalPages(itemsTotal, perPage));

  // =========================================================================
  // List view
  // =========================================================================

  var renderList = function () {
    return React.createElement(React.Fragment, null,

      React.createElement('div', { className: 'page-header-row' },
        React.createElement('div', { className: 'page-header-main' },
          React.createElement('div', { className: 'page-header-title-row' },
            React.createElement('div', { className: 'page-header-icon' }, React.createElement(GiftTagIcon, { size: 18 })),
            React.createElement('div', { className: 'ptitle' }, 'Gift Packing')
          ),
          React.createElement('div', { className: 'header-subtitle' }, 'Manage gift wrapping options and pricing')
        ),
        React.createElement('button', { className: 'gp-btn gp-btn-primary', type: 'button', onClick: goToAdd },
          React.createElement(PlusIcon, null), 'Add Gift Packing')
      ),

      // -- summary cards --
      React.createElement('div', { className: 'gp-summary-grid' },
        React.createElement(SummaryCard, {
          icon: React.createElement(GiftTagIcon, { size: 20 }), iconClass: 'gp-card-icon-types',
          label: 'Total Gift Packing Types', value: formatNum(summary && summary.totalGiftPackingTypes), hint: 'All gift packing types',
          isActive: statusFilter === 'all',
          onClick: function () { setStatusFilter('all'); setPage(1); }
        }),
        React.createElement(SummaryCard, {
          icon: React.createElement(ToggleCircleIcon, { size: 20 }), iconClass: 'gp-card-icon-active',
          label: 'Active Gift Packing Types', value: formatNum(summary && summary.activeGiftPackingTypes), hint: 'Currently active',
          isActive: statusFilter === 'active',
          onClick: function () { setStatusFilter('active'); setPage(1); }
        }),
        React.createElement(SummaryCard, {
          icon: React.createElement(BagIcon, { size: 20 }), iconClass: 'gp-card-icon-orders',
          label: 'Orders With Gift Packing', value: formatNum(summary && summary.ordersWithGiftPacking), hint: 'Total orders',
          isActive: statusFilter === 'orders',
          onClick: function () { setStatusFilter('orders'); setPage(1); }
        }),
        React.createElement(SummaryCard, {
          icon: React.createElement(RupeeStackIcon, { size: 20 }), iconClass: 'gp-card-icon-revenue',
          label: 'Total Gift Packing Revenue', value: formatINR(summary && summary.totalGiftPackingRevenue), hint: 'Total revenue',
          isActive: statusFilter === 'revenue',
          onClick: function () { setStatusFilter('revenue'); setPage(1); }
        })
      ),

      // -- table card --
      React.createElement('div', { className: 'gp-card' },
        React.createElement('div', { className: 'gp-card-hdr' },
          React.createElement('div', { className: 'gp-card-hdr-title' },
            React.createElement(GiftTagIcon, { size: 17 }),
            React.createElement('h3', null, 'Gift Packing Types')
          )
        ),

        itemsError && React.createElement('div', { className: 'gp-error-banner' }, itemsError),

        React.createElement('div', { className: 'gp-toolbar' },
          React.createElement('div', { className: 'gp-search-field' },
            React.createElement(SearchIcon, null),
            React.createElement('input', {
              placeholder: 'Search gift packing\u2026', value: search,
              onChange: function (e) { setSearch(e.target.value); },
            })
          ),
          React.createElement('select', {
            className: 'gp-status-select',
            value: statusFilter,
            onChange: function (e) { setStatusFilter(e.target.value); setPage(1); },
          },
            STATUS_FILTERS.map(function (s) { return React.createElement('option', { key: s.value, value: s.value }, s.label); })
          )
        ),

        React.createElement('div', { className: 'gp-table-scroll' },
          React.createElement('table', { className: 'tbl' },
            React.createElement('thead', null,
              React.createElement('tr', null,
                React.createElement('th', null, 'S.No'),
                React.createElement('th', null, 'Gift Packing Name'),
                React.createElement('th', null, 'Image'),
                React.createElement('th', null, 'Description'),
                React.createElement('th', null, 'Price (\u20B9)'),
                React.createElement('th', null, 'Status'),
                React.createElement('th', null, 'Actions')
              )
            ),
            React.createElement('tbody', null,
              itemsLoading
                ? React.createElement('tr', null, React.createElement('td', { colSpan: 7, className: 'gp-table-empty' }, 'Loading gift packing types\u2026'))
                : items.length === 0
                  ? React.createElement('tr', null, React.createElement('td', { colSpan: 7, className: 'gp-table-empty' }, 'No gift packing types found.'))
                  : items.map(function (it, idx) {
                      var active = isActiveStatus(it.status);
                      var imgUrl = resolveImageUrl(it.image || it.imageUrl);
                      return React.createElement('tr', { key: it._id || it.id || idx },
                        React.createElement('td', { style: { fontSize: 12 } }, ((page - 1) * perPage) + idx + 1),
                        React.createElement('td', null, React.createElement('span', { style: { fontWeight: 700 } }, it.name || '\u2014')),
                        React.createElement('td', null,
                          imgUrl
                            ? React.createElement('img', { className: 'gp-thumb', src: imgUrl, alt: it.name || 'Gift packing' })
                            : React.createElement('div', { className: 'gp-thumb gp-thumb-placeholder' }, React.createElement(GiftTagIcon, { size: 16 }))
                        ),
                        React.createElement('td', { className: 'desc-cell', title: it.description }, it.description || '\u2014'),
                        React.createElement('td', null, formatINR(it.price)),
                        React.createElement('td', null,
                          React.createElement('span', { className: 'gp-badge ' + (active ? 'gp-badge-active' : 'gp-badge-inactive') }, active ? 'Active' : 'Inactive')
                        ),
                        React.createElement('td', { className: 'actions-cell' },
                          React.createElement('button', { className: 'gp-icon-btn', type: 'button', title: 'View', onClick: function () { goToEdit(it._id || it.id); } }, React.createElement(EyeIcon, null)),
                          React.createElement('button', { className: 'gp-icon-btn', type: 'button', title: 'Edit', onClick: function () { goToEdit(it._id || it.id); } }, React.createElement(PencilIcon, null)),
                          React.createElement('button', { className: 'gp-icon-btn', type: 'button', title: 'Delete', onClick: function () { handleDelete(it); } }, React.createElement(TrashIcon, null))
                        )
                      );
                    })
            )
          )
        ),

        !itemsLoading && items.length > 0 && React.createElement('div', { className: 'gp-table-footer' },
          React.createElement('span', { className: 'gp-pagination-info' },
            'Showing ', ((page - 1) * perPage) + 1, ' to ', Math.min(((page - 1) * perPage) + items.length, itemsTotal), ' of ', itemsTotal, ' entries'
          ),
          React.createElement(Pagination, { page: page, pages: itemsPages, onChange: setPage }),
          React.createElement('div', { className: 'gp-rows-per-page' },
            React.createElement('span', null, 'Rows per page:'),
            React.createElement('select', {
              value: perPage,
              onChange: function (e) { setPerPage(Number(e.target.value)); setPage(1); },
            },
              GIFT_PER_PAGE_OPTIONS.map(function (n) { return React.createElement('option', { key: n, value: n }, n); })
            )
          )
        )
      )
    );
  };

  // =========================================================================
  // Add / Edit form view
  // =========================================================================

  var renderForm = function () {
    return React.createElement(GiftPackingForm, {
      mode: view, // 'add' | 'edit'
      itemId: activeItemId,
      onBack: goToList,
      onSaved: function (msg, isEditMode) {
        if (isEditMode) {
          setShowUpdateSuccess(true);
        } else {
          showToast('success', msg);
        }
        refreshAll();
        goToList();
      },
    });
  };

  return React.createElement(AdminLayout, { hideLayoutOnModal: showUpdateSuccess },
    React.createElement(Toast, { toast: toast, onClose: function () { setToast(null); } }),
    view === 'list' && renderList(),
    (view === 'add' || view === 'edit') && renderForm(),
    showUpdateSuccess && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: 'Updated successfully!',
      onClose: function () { setShowUpdateSuccess(false); }
    })
  );
};

// ---------------------------------------------------------------------------
// Add / Edit Gift Packing form
// ---------------------------------------------------------------------------

var GiftPackingForm = function (props) {
  var mode = props.mode; var itemId = props.itemId; var onBack = props.onBack; var onSaved = props.onSaved;
  var isEdit = mode === 'edit';

  var nameState = useState(''); var name = nameState[0]; var setName = nameState[1];
  var nameErrorState = useState(''); var nameError = nameErrorState[0]; var setNameError = nameErrorState[1];
  var descriptionState = useState(''); var description = descriptionState[0]; var setDescription = descriptionState[1];
  var priceState = useState(''); var price = priceState[0]; var setPrice = priceState[1];
  var statusState = useState('active'); var status = statusState[0]; var setStatus = statusState[1];

  var imageFileState = useState(null); var imageFile = imageFileState[0]; var setImageFile = imageFileState[1];
  var imagePreviewState = useState(''); var imagePreview = imagePreviewState[0]; var setImagePreview = imagePreviewState[1];
  var dragOverState = useState(false); var dragOver = dragOverState[0]; var setDragOver = dragOverState[1];

  var loadingState = useState(isEdit); var loading = loadingState[0]; var setLoading = loadingState[1];
  var savingState = useState(false); var saving = savingState[0]; var setSaving = savingState[1];
  var errorState = useState(''); var formError = errorState[0]; var setFormError = errorState[1];

  useEffect(function () {
    if (!isEdit || !itemId) return;
    setLoading(true);
    getGiftPackingDetails(itemId)
      .then(function (res) {
        var d = (res && res.data && res.data.data) ? res.data.data : (res && res.data) || {};
        setName(d.name || '');
        setDescription(d.description || '');
        setPrice(d.price !== undefined ? String(d.price) : '');
        setStatus(isActiveStatus(d.status) ? 'active' : 'inactive');
        var imgUrl = resolveImageUrl(d.image || d.imageUrl || '');
        setImagePreview(imgUrl);
      })
      .catch(function (e) {
        console.error('Failed to load gift packing details:', e);
        var msg = (e && e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Failed to load this gift packing option.';
        setFormError(msg);
      })
      .finally(function () { setLoading(false); });
  }, [isEdit, itemId]);

  var handleFileChange = function (file) {
    if (!file) return;
    setImageFile(file);
    var reader = new FileReader();
    reader.onload = function (e) { setImagePreview(e.target.result); };
    reader.readAsDataURL(file);
  };

  var handleNameKeyDown = function (e) {
    var allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (allowedKeys.indexOf(e.key) !== -1 || e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    if (e.key.length === 1 && !/^[a-zA-Z\s]$/.test(e.key)) {
      e.preventDefault();
      setNameError('Gift Packing Name can only contain letters and spaces.');
    }
  };

  var handleNameChange = function (e) {
    var rawVal = e.target.value;
    if (/[^a-zA-Z\s]/.test(rawVal)) {
      setNameError('Gift Packing Name can only contain letters and spaces.');
    } else {
      setNameError('');
    }
    var cleanVal = rawVal.replace(/[^a-zA-Z\s]/g, '');
    setName(cleanVal);
  };

  var validate = function () {
    if (!name.trim()) return 'Please enter a gift packing name';
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) return 'Gift Packing Name can only contain letters and spaces';
    if (price === '' || isNaN(Number(price)) || Number(price) < 0) return 'Please enter a valid price';
    if (!isEdit && !imageFile && !imagePreview) return 'Please upload a gift packing image';
    return '';
  };

  var handleSubmit = function () {
    var err = validate();
    if (err) { setFormError(err); return; }
    setFormError('');
    setSaving(true);

    var fd = new FormData();
    fd.append('name', name.trim());
    fd.append('description', description.trim());
    fd.append('price', String(Number(price)));
    fd.append('status', status);
    if (imageFile) {
      fd.append('image', imageFile);
    } else if (imagePreview && !imagePreview.startsWith('data:')) {
      fd.append('image', imagePreview);
    }

    var request = isEdit ? updateGiftPacking(itemId, fd) : createGiftPacking(fd);
    request
      .then(function (res) {
        var msg = (res && res.data && res.data.message) || (isEdit ? 'Gift packing option updated successfully!' : 'Gift packing option added successfully!');
        onSaved(msg, isEdit);
      })
      .catch(function (e) {
        var msg = (e.response && e.response.data && (e.response.data.message || e.response.data.msg)) || 'Failed to save gift packing option';
        setFormError(msg);
        console.error('Save Gift Packing error:', (e.response && e.response.data) || e.message);
      })
      .finally(function () { setSaving(false); });
  };

  return React.createElement(React.Fragment, null,
    React.createElement(Breadcrumb, {
      items: [
        { label: 'Gift Packing', onClick: onBack },
        { label: isEdit ? 'Edit Gift Packing' : 'Add Gift Packing' },
      ],
    }),

    React.createElement('button', { className: 'gp-back-link', type: 'button', onClick: onBack },
      React.createElement(ArrowLeftIcon, null), ' Back to Gift Packing'
    ),

    React.createElement('div', { className: 'page-header-row' },
      React.createElement('div', { className: 'page-header-main' },
        React.createElement('div', { className: 'ptitle' }, isEdit ? 'Edit Gift Packing' : 'Add Gift Packing'),
        React.createElement('div', { className: 'header-subtitle' }, isEdit ? 'Update wrapping option' : 'Add new gift wrapping option')
      )
    ),

    loading
      ? React.createElement('div', { className: 'gp-card gp-form-card' }, React.createElement('div', { className: 'gp-table-empty' }, 'Loading\u2026'))
      : React.createElement('div', { className: 'gp-card gp-form-card' },
          formError && React.createElement('div', { className: 'gp-form-error' }, React.createElement(AlertCircleIcon, null), formError),

          React.createElement('div', { className: 'gp-form-grid' },

            React.createElement('div', { className: 'gp-field gp-field-full' },
              React.createElement('label', null, 'Gift Packing Name *'),
              React.createElement('input', {
                placeholder: 'Enter gift packing name',
                value: name,
                onKeyDown: handleNameKeyDown,
                onChange: handleNameChange,
              }),
              nameError && React.createElement('div', { className: 'gp-field-hint-error' }, nameError)
            ),

            React.createElement('div', { className: 'gp-field gp-field-full' },
              React.createElement('label', null, 'Description'),
              React.createElement('textarea', {
                placeholder: 'Enter description', rows: 4,
                value: description, onChange: function (e) { setDescription(e.target.value); },
              })
            ),

            React.createElement('div', { className: 'gp-field gp-field-full' },
              React.createElement('label', null, 'Gift Packing Image', !isEdit && ' *'),
              React.createElement('div', {
                className: 'gp-dropzone' + (dragOver ? ' is-dragover' : ''),
                onDragOver: function (e) { e.preventDefault(); setDragOver(true); },
                onDragLeave: function () { setDragOver(false); },
                onDrop: function (e) {
                  e.preventDefault(); setDragOver(false);
                  var file = e.dataTransfer.files && e.dataTransfer.files[0];
                  if (file) handleFileChange(file);
                },
                onClick: function () { document.getElementById('gp-file-input').click(); },
              },
                imagePreview
                  ? React.createElement('img', { className: 'gp-dropzone-preview', src: imagePreview, alt: 'Gift packing preview' })
                  : React.createElement('div', { className: 'gp-dropzone-empty' },
                      React.createElement(UploadCloudIcon, null),
                      React.createElement('span', null, 'Click to upload image'),
                      React.createElement('span', { className: 'gp-dropzone-hint' }, 'or drag and drop'),
                      React.createElement('span', { className: 'gp-dropzone-hint' }, 'PNG, JPG up to 2MB')
                    ),
                imagePreview && React.createElement('button', {
                  type: 'button', className: 'gp-btn gp-btn-ghost gp-btn-sm gp-change-image-btn',
                  onClick: function (e) { e.stopPropagation(); document.getElementById('gp-file-input').click(); },
                }, 'Change Image')
              ),
              React.createElement('input', {
                id: 'gp-file-input', type: 'file', accept: 'image/png,image/jpeg', style: { display: 'none' },
                onChange: function (e) { handleFileChange(e.target.files && e.target.files[0]); },
              })
            ),

            React.createElement('div', { className: 'gp-field' },
              React.createElement('label', null, 'Price (\u20B9) *'),
              React.createElement('input', {
                type: 'number', min: 0, step: '0.01', placeholder: '0.00',
                value: price, onChange: function (e) { setPrice(e.target.value); },
              })
            ),

            React.createElement('div', { className: 'gp-field' },
              React.createElement('label', null, 'Status *'),
              React.createElement('select', {
                value: status, onChange: function (e) { setStatus(e.target.value); },
              },
                React.createElement('option', { value: 'active' }, 'Active'),
                React.createElement('option', { value: 'inactive' }, 'Inactive')
              )
            ),

            React.createElement('div', { className: 'gp-field gp-field-toggle' },
              React.createElement('label', null, 'Available for Customers'),
              React.createElement('label', { className: 'gp-toggle' },
                React.createElement('input', {
                  type: 'checkbox', checked: status === 'active',
                  onChange: function (e) { setStatus(e.target.checked ? 'active' : 'inactive'); },
                }),
                React.createElement('span', { className: 'gp-toggle-track' }, React.createElement('span', { className: 'gp-toggle-thumb' }))
              )
            )
          ),

          React.createElement('div', { className: 'gp-form-actions' },
            React.createElement('button', { className: 'gp-btn gp-btn-ghost', type: 'button', onClick: onBack, disabled: saving }, 'Cancel'),
            React.createElement('button', {
              className: 'gp-btn gp-btn-primary', type: 'button', disabled: saving, onClick: handleSubmit,
            }, saving ? 'Saving\u2026' : (isEdit ? 'Save Changes' : 'Save Gift Packing'))
          )
        )
  );
};

export default GiftPacking;